import { PublicShell } from "@/components/public/public-shell";
import { btn } from "@/components/ds/styles";
import { createFileRoute, Link } from "@tanstack/react-router";
import { queryOptions, useMutation, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { listMyTraumaCourses, startTraumaRetake } from "@/lib/trauma/attempts.functions";
import { getTraumaGate } from "@/lib/trauma/gate.functions";
import { TRAUMA_COURSES_UI as UI } from "@/lib/trauma/ui-copy";
import { myTraumaCourseView } from "@/lib/trauma/my-courses";
import { arabicDate } from "@/lib/arabic-format";
import { RouteError, RouteNotFound } from "@/components/route-boundaries";
import { LEVEL_STYLE } from "@/lib/trauma/axis-style";
import type { TraumaLevelKey } from "@/lib/trauma/schema";
import { cn } from "@/lib/utils";

const myCoursesQuery = () => queryOptions({
  queryKey: ["trauma", "my-courses"],
  queryFn: () => listMyTraumaCourses(),
  gcTime: 0,
  retry: false,
});

export const Route = createFileRoute("/_authenticated/portal/courses")({
  loader: ({ context }) => context.queryClient.ensureQueryData(myCoursesQuery()),
  gcTime: 0,
  head: () => ({ meta: [
    { title: `${UI.title} — د. سارة النحّاس` },
    { name: "description", content: "دوراتك في صدمات الطفولة مع د. سارة النحّاس." },
    { property: "og:title", content: `${UI.title} — د. سارة النحّاس` },
    { property: "og:description", content: "دوراتك في صدمات الطفولة." },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary" },
    { name: "robots", content: "noindex" },
  ] }),
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
  component: () => (
    <PublicShell ground="bg-cream-200">
      <MyCoursesPage />
    </PublicShell>
  ),
});

const primaryBtn = `${btn.primary}`;
const secondaryBtn = "inline-flex min-h-12 items-center justify-center rounded-ds-pill bg-amber-100 px-5 font-display text-[15px] font-bold text-brown-700 transition-colors duration-ds-fast hover:bg-amber-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60";

function MyCoursesPage() {
  const initialData = Route.useLoaderData();
  const { data } = useSuspenseQuery({ ...myCoursesQuery(), initialData });
  const { data: gate } = useQuery({ queryKey: ["trauma", "gate"], queryFn: () => getTraumaGate(), staleTime: 60_000, retry: false });
  const navigate = Route.useNavigate();
  const retake = useServerFn(startTraumaRetake);
  const restart = useMutation({
    mutationFn: (courseId: string) => retake({ data: { courseId } }),
    onSuccess: (_r, courseId) => navigate({ to: "/courses/trauma/$id/play", params: { id: courseId } }),
    onError: () => toast.error(UI.retryError),
  });
  const courses = data.filter((c) => c.canOpen).sort((a, b) => a.order - b.order);

  return (
    <div dir="rtl" className="font-body text-[17px] leading-[1.85] text-text-primary">
      <div className="mx-auto w-full max-w-3xl px-5 py-10 lg:py-14">
        <Link to="/portal" className="inline-flex min-h-12 items-center font-display text-[14px] font-bold text-amber-800">لوحتي</Link>
        <h2 className="mt-1 font-display text-[30px] font-extrabold">دوراتك</h2>
        {courses.length === 0 ? (
          <div className="mt-6 divide-y divide-hairline-subtle border-y border-hairline-subtle">
            <p className="py-5 text-[17px] text-brown-500">
              {UI.empty}
              {gate?.canView && <> <Link to="/courses" className="font-bold text-amber-800 underline underline-offset-4">{UI.browse}</Link></>}
            </p>
            <Link to="/book" className="flex min-h-14 items-center py-4 font-display text-[17px] font-bold text-brown-700 hover:text-amber-800">حجز استشارة ←</Link>
          </div>
        ) : (
          <ul className="mt-6 divide-y divide-hairline-subtle">
            {courses.map((c) => {
              const v = myTraumaCourseView(c);
              return (
                <li key={c.id} className="py-6">
                  <h3 className="font-display text-[19px] font-extrabold">{c.title}</h3>
                  {c.subtitle && <p className="mt-1 text-[14px] text-brown-500">{c.subtitle}</p>}
                  {v.state === "in_progress" && v.answered !== null && (
                    <div className="mt-4">
                      <p className="text-[14px] font-bold text-brown-500">{UI.progress(v.answered)}</p>
                      <div className="mt-1 h-2 overflow-hidden rounded-ds-pill bg-cream-300" aria-hidden>
                        <div className="h-full rounded-ds-pill bg-green-500" style={{ width: `${Math.round((v.answered / 16) * 100)}%` }} />
                      </div>
                    </div>
                  )}
                  {c.last && (() => {
                    const style = LEVEL_STYLE[c.last.levelKey as TraumaLevelKey];
                    const Icon = style?.Icon;
                    return (
                      <p className={cn("mt-3 inline-flex flex-wrap items-center gap-2 rounded-ds-pill px-4 py-1 text-[14px] font-bold", style?.surface ?? "bg-cream-100")}>
                        {Icon ? <Icon className="size-4 shrink-0" aria-hidden /> : null}
                        {UI.lastResult}: {c.last.levelTitle} · {arabicDate(c.last.completed_at)}
                      </p>
                    );
                  })()}
                  <div className="mt-4 flex flex-wrap gap-3">
                    {v.primary && (
                      <Link to="/courses/trauma/$id/play" params={{ id: c.id }} className={primaryBtn}>
                        {v.primary === "resume" ? UI.resume : UI.start}
                      </Link>
                    )}
                    {v.showResult && v.lastAttemptId && (
                      <Link to="/courses/trauma/$id/result/$attemptId" params={{ id: c.id, attemptId: v.lastAttemptId }} className={v.primary ? secondaryBtn : primaryBtn}>{UI.viewResult}</Link>
                    )}
                    {v.showLessons && (
                      <Link to="/courses/trauma/$id/lessons" params={{ id: c.id }} className={secondaryBtn}>{UI.readLessons}</Link>
                    )}
                    {v.showRetake && (
                      <button type="button" disabled={restart.isPending} onClick={() => restart.mutate(c.id)} className={secondaryBtn}>{UI.retake}</button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
